import clsx from 'clsx';
import { unescape } from 'lodash';
import React from 'react';
import toast from 'react-hot-toast';
import ReactHtmlParser from 'react-html-parser';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import ForumIcon from 'images/forum/forum_icon.svg';
import linkAnchorIcon from 'images/forum/link_anchor.svg';

import { useAppContext } from '../../../contexts/AppContext';
import {
  useCurrentUser,
  useForumPost,
  useForumPostDelete,
  useScrollToAnchor,
} from '../../../hooks';
import { edit_forum_post_path, forum_path } from '../../../routes';
import { CommentUser, CommentableType } from '../../../utils/backend-api/comments';
import { FORUM_POSTS_KEY } from '../../../utils/backend-api/forumPosts';
import CommentAvatar from '../../Comments/CommentAvatar';
import CommentsSection from '../../Comments/CommentsSection';
import UserProfileLink from '../../User/UserProfileLink';
import { userName, userTitle } from '../../User/userHelpers';
import { FlexLink, TimeAgo } from '../../shared';
import MetaTags from '../../shared/MetaTags';
import ForumGhostButton from '../ForumGhostButton';
import { FORUM_BASE_TITLE } from '../ForumLayout';
import ForumPanel from '../ForumPanel';
import ForumPostActions from '../ForumPostActions';
import ForumTags from '../ForumTags';
import GatedContent from '../GatedContent';
import ForumPostScreenshot, { useForumPostScreenshot } from './ForumPostScreenshot';
import styles from './styles.module.css';

export function forumTitleTag(title) {
  return [title, FORUM_BASE_TITLE].join(' — ');
}

function UserInfo({ user }: { user: CommentUser }) {
  return (
    <div className={styles.userInfo}>
      <CommentAvatar user={user} className={styles.commentAvatar} />
      <div className={styles.userInfoDetails}>
        <div className={styles.userInfoPostedBy}>
          Posted by <UserProfileLink user={user}>{userName(user)}</UserProfileLink>
        </div>
        <div>{userTitle(user)}</div>
      </div>
    </div>
  );
}

export default function ForumPostPage() {
  const { isWebview } = useAppContext();
  const params = useParams();
  const { forumPost, isForumPostLoading } = useForumPost(params.id);
  const { currentUser } = useCurrentUser();
  const { mutate: deletePost } = useForumPostDelete();
  const isLoading = isForumPostLoading || !forumPost;
  useScrollToAnchor(isLoading);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { onDownloadScreenshot, renderScreenshotContent } = useForumPostScreenshot(
    forumPost?.title
  );

  // TODO: Handle loading error
  if (isLoading) return null;

  const { comment } = forumPost;
  const { user } = comment;

  const onDeletePost = () => {
    window.confirm('Are you sure you want to delete this post?') &&
      deletePost(forumPost.id, {
        onSuccess: () => {
          toast.success('Your post has been deleted.');
          queryClient.invalidateQueries(FORUM_POSTS_KEY);
          navigate(forum_path());
          window.scrollTo(0, 0);
        },
        onError: (e) => {
          console.error(e);
          toast.error('Post was not deleted, please try again.');
        },
      });
  };

  const description = unescape(forumPost.comment.snippet);
  const body = ReactHtmlParser(comment.body);

  return (
    <>
      <MetaTags
        title={forumTitleTag(forumPost.title)}
        ogSiteName="The Information Forum"
        ogTitle={forumPost.title}
        twitterTitle={forumPost.title}
        description={description}
        ogImagePath="/forum/twitter_forum.png"
      />
      {currentUser?.can_view_forum_posts && !!renderScreenshotContent && (
        <ForumPostScreenshot
          title={forumPost.title}
          body={body}
          renderScreenshotContent={renderScreenshotContent}
          length={comment.body.length}
        />
      )}

      <div className={styles.forumPostPage}>
        <div className="container container-padded">
          <ForumPanel>
            <div className={styles.forumPostPageHeader}>
              <h1 className={styles.forumPostTitle}>{forumPost.title}</h1>
              {!!currentUser && currentUser.id === user.id && (
                <div className={styles.forumPostPageActions}>
                  <div className={styles.forumPostPageEditActionContainer}>
                    <FlexLink
                      href={edit_forum_post_path(forumPost.id)}
                      className={styles.forumPostPageEditAction}
                    >
                      Edit
                    </FlexLink>
                  </div>
                  <div className={styles.forumPostPageEditActionContainer}>
                    <button
                      onClick={onDeletePost}
                      className={clsx('unstyled', styles.forumPostPageEditAction)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className={styles.forumPostSection}>
              <div className={styles.subHeader}>
                <UserInfo user={user} />
                <TimeAgo className={styles.forumPostDate} date={comment.created_at} />
              </div>
            </div>
            {currentUser?.can_view_forum_posts ? (
              <>
                <div className={clsx(styles.forumPostBody, styles.forumPostBody)}>{body}</div>
                {!!forumPost.external_url && (
                  <div className={clsx(styles.forumPostSection, styles.forumPostLinkContainer)}>
                    <img className={styles.linkAnchor} src={linkAnchorIcon} alt="link anchor" />
                    <a
                      href={forumPost.external_url}
                      className={styles.forumPostExternalUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {forumPost.external_url}
                    </a>
                  </div>
                )}
                {forumPost.manual_entities.length > 0 && (
                  <div className={styles.forumPostSection}>
                    <ForumTags tags={forumPost.manual_entities} />
                  </div>
                )}
                {!!comment.edited_at && (
                  <div className={clsx(styles.forumPostSection, styles.forumPostEditedAt)}>
                    Edited <TimeAgo date={comment.edited_at} />
                  </div>
                )}
                <div className={styles.forumPostActions}>
                  <ForumPostActions
                    forumPost={forumPost}
                    onDownloadScreenshot={onDownloadScreenshot}
                  />
                  {!isWebview && (
                    <ForumGhostButton
                      customStyles={styles.ghostBtn}
                      linkUrl={forum_path()}
                      svgIcon={ForumIcon}
                      text={'Back to Forum'}
                    />
                  )}
                </div>
              </>
            ) : (
              <div className={styles.gatedContentContainer}>
                <GatedContent
                  headline="Join now to read the full story"
                  leadType="forum_post"
                  trackingProps={{
                    'lead-details': JSON.stringify({ forum_post_title: forumPost.title }),
                  }}
                  emailOnly={true}
                />
              </div>
            )}
          </ForumPanel>
          {currentUser?.can_view_forum_posts && (
            <ForumPanel id="comments">
              <CommentsSection
                commentableId={forumPost.id}
                commentableType={CommentableType.ForumPost}
              />
            </ForumPanel>
          )}
        </div>
      </div>
    </>
  );
}
